/*
 * Editor document in, markdown out — the half of a dialect that has to be exact. What a dialect
 * shares with every other lives here: the writers whose markdown does not depend on the dialect at
 * all (a paragraph, a quote, a rule, an image, the four inline markers), the two pieces of
 * arithmetic that are easy to get subtly wrong (how long a fence has to be, what a line break
 * becomes in a block that markdown writes on one line), and the state fields prosemirror-markdown
 * carries but does not declare.
 *
 * Everything a dialect decides for itself — how a heading, a list, a table, a code block and a link
 * are written — is a writer of its own, because those are exactly the places where "the same
 * markdown" and "the same bytes" part company.
 */

import {
	defaultMarkdownSerializer,
	MarkdownSerializer,
	type MarkdownSerializerState,
} from "prosemirror-markdown"
import { Fragment, type Node, type NodeType, type Schema } from "prosemirror-model"

/** prosemirror-markdown does not export the type of a mark's writer; this is it. */
export type MarkWriter = (typeof defaultMarkdownSerializer)["marks"][string]

export type NodeWriter = (
	state: MarkdownSerializerState,
	node: Node,
	parent: Node,
	index: number,
) => void

/**
 * The state fields prosemirror-markdown carries but does not declare — its type only lists the
 * methods. Writing the output buffer directly is what a line break needs (a `write` would put the
 * block delimiter on the wrong side of the newline), and `atBlockStart` is what makes `- ` after a
 * break stay text rather than starting a list.
 */
export interface WritableState extends MarkdownSerializerState {
	out: string
	atBlockStart: boolean
	inAutolink: boolean | undefined
}

export interface SerializerSpec {
	nodes: Record<string, NodeWriter>
	marks: Record<string, MarkWriter>
	/** prosemirror-markdown's own options: the extra characters to escape, above all. */
	options?: ConstructorParameters<typeof MarkdownSerializer>[2]
	/** What `serialize` passes per call; `tightLists` is the one that matters. */
	serializeOptions?: Parameters<MarkdownSerializer["serialize"]>[1]
}

/** The longest run of backticks in a code block's text: the fence has to be longer than all of them. */
export function fenceFor(text: string): string {
	let longest = 0
	for (const run of text.matchAll(/`+/g)) longest = Math.max(longest, run[0].length)
	return "`".repeat(Math.max(3, longest + 1))
}

/**
 * The node's content with every break turned into a space: what a block markdown writes on a single
 * line — a heading, a table cell — can hold, and the only faithful reading of a break inside one.
 */
export function withoutBreaks(node: Node, breakType: NodeType, schema: Schema): Node {
	const out: Node[] = []
	node.forEach((child) => {
		out.push(child.type === breakType ? schema.text(" ", child.marks) : child)
	})
	return node.copy(Fragment.fromArray(out))
}

/** The writers no dialect changes: what they write is markdown's, not the dialect's. */
export function sharedNodeWriters(): Record<string, NodeWriter> {
	return {
		paragraph(state, node) {
			state.renderInline(node)
			state.closeBlock(node)
		},

		blockquote(state, node) {
			state.wrapBlock("> ", null, node, () => state.renderContent(node))
		},

		list_item(state, node) {
			state.renderContent(node)
		},

		horizontal_rule(state, node) {
			state.write("---")
			state.closeBlock(node)
		},

		image(state, node) {
			const src = (node.attrs.src as string).replace(/[()]/g, "\\$&")
			const title = node.attrs.title as string
			const suffix = title === "" ? "" : ` "${title.replaceAll('"', '\\"')}"`
			state.write(`![${state.esc(node.attrs.alt as string)}](${src}${suffix})`)
		},
	}
}

/** The four inline markers every dialect writes the same way; a link is never one of them. */
export function sharedMarkWriters(): Record<string, MarkWriter> {
	return {
		em: { open: "*", close: "*", mixable: true, expelEnclosingWhitespace: true },
		strong: { open: "**", close: "**", mixable: true, expelEnclosingWhitespace: true },
		strike: { open: "~~", close: "~~", mixable: true, expelEnclosingWhitespace: true },
		// The backtick run of a code span depends on what is inside it, and its content is never
		// escaped; that arithmetic is prosemirror-markdown's own and there is no second version of
		// it here.
		code: defaultMarkdownSerializer.marks.code ?? { open: "`", close: "`" },
	}
}

export function buildSerializer(spec: SerializerSpec): (doc: Node) => string {
	const serializer = new MarkdownSerializer(spec.nodes, spec.marks, spec.options)
	return (doc) => serializer.serialize(doc, spec.serializeOptions)
}
